<template>
    <div
        v-if="active"
        data-popup
        class="fixed inset-0 z-9999 flex items-center justify-center p-6"
    >
        <div
            class="bg-black-opacity-50 absolute inset-0 z-10"
            @click="dismissPopup"
        />

        <div class="relative z-20 flex max-h-[570px] w-full max-w-[470px] flex-col">
            <DButton
                class="absolute! -top-12 right-0 z-10"
                data-mode="base-invert"
                theme="transparent"
                icon="ph:x"
                @click="dismissPopup"
            />

            <div
                :data-theme="data.colorTheme"
                :data-mode="mode"
                class="surface-primary rounded-modal-md flex flex-col overflow-hidden shadow-xl"
            >
                <div class="relative flex-1 overflow-hidden">
                    <img
                        v-if="!isEmpty(popup.image)"
                        :src="popup.image"
                        class="h-full w-full object-cover"
                    >
                </div>
                <div class="flex-1 shrink-0 space-y-4 px-6 py-4">
                    <div class="space-y-1">
                        <h2 class="leading-lg font-brand-heading text-neutral-base font-heading text-lg">
                            {{ popup.heading }}
                        </h2>

                        <div
                            v-if="popupEntry"
                            class="space-y-2 pt-2"
                        >
                            <small
                                v-if="popupEntry.startDate"
                                class="text-neutral-base leading-sm font-ux flex items-start space-x-2 text-sm lg:items-center"
                            >
                                <DIcon
                                    icon="ph:calendar-blank"
                                    size="w-5 h-5"
                                    class="shape-brand-accent"
                                />
                                <DateFormat
                                    :date="popupEntry.startDate"
                                    :end="popupEntry.endDate"
                                />
                            </small>

                            <small
                                v-if="popupEntry.startDate && !popupEntry.endDate"
                                class="text-neutral-base leading-sm font-ux flex items-start space-x-2 text-sm lg:items-center"
                            >
                                <DIcon
                                    icon="ph:clock"
                                    size="w-5 h-5"
                                    class="shape-brand-accent"
                                />
                                <DateFormat
                                    :date="popupEntry.startDate"
                                    :end="popupEntry.endDate"
                                    format="LT"
                                />
                            </small>

                            <small
                                v-if="popupEntry.location"
                                class="text-neutral-base leading-sm font-ux hidden items-start space-x-2 text-sm md:flex lg:items-center"
                            >
                                <DIcon
                                    icon="ph:map-pin"
                                    size="w-5 h-5"
                                    class="shape-brand-accent"
                                />
                                <span>{{ popupEntry.location }}</span>
                            </small>
                        </div>

                        <h3
                            v-if="popup.subheading"
                            class="text-neutral-base text-sm font-normal"
                        >
                            {{ popup.subheading }}
                        </h3>
                    </div>

                    <p class="font-ux line-clamp-5 text-sm whitespace-pre-wrap text-(--text-body)">
                        {{ popup.bodyText }}
                    </p>
                </div>

                <div class="mt-auto flex flex-col-reverse gap-3 px-6 py-3 md:flex-row">
                    <DButton
                        theme="base"
                        wide
                        @click="dismissPopup"
                    >
                        Dismiss
                    </DButton>
                    <NuxtLink
                        v-if="popup.button && popup.button.url"
                        :to="popup.button.url"
                        :target="popup.button.target"
                        class="w-full flex-1"
                    >
                        <DButton
                            wide
                            @click="markAsRead"
                        >
                            {{ popup.button.text }}
                        </DButton>
                    </NuxtLink>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { DButton, DIcon } from '@digistorm/spark'
    import { useScrollLock } from '@vueuse/core'

    const { cmsQuery } = useGraphQL()
    const scrollLocked = useScrollLock(window)
    const { entry } = storeToRefs(useEntryStore())

    const active = ref(false)

    const data = ref(null)

    const popupData = computed(() => {
        return first(entry.value?.popup)
    })

    const cookieName = `popup:${popupData.value?.canonicalUid}`
    const popupRead = useCookie(cookieName)

    const popupContent = computed(() => {
        return get(data.value, 'ds_PopupsEntries.0', {})
    })

    // this is only for the articleOrEvent content type
    const popupEntry = computed(() => {
        return get(popupContent.value, 'entry.0', {})
    })

    const popup = computed(() => {
        let content = {}

        if (popupContent.value.contentType === 'articleOrEvent') {
            if (isEmpty(popupEntry.value)) {
                return null
            }

            content = {
                image: popupEntry.value.image[0]?.url,
                heading: popupEntry.value.title,
                bodyText: popupEntry.value.eventExcerpt ?? popupEntry.value.articleExcerpt,
                button: {
                    text: 'Read more',
                    url: popupEntry.value.url,
                    target: '',
                },
            }
        } else {
            content = {
                image: get(popupContent.value, 'image.0.url'),
                heading: popupContent.value.heading,
                subheading: popupContent.value.subheading,
                bodyText: popupContent.value.bodyText,
            }

            if (has(popupContent.value, 'button')) {
                content.button = {
                    url: get(popupContent.value.button, '0.url'),
                    text: get(popupContent.value.button, '0.text', 'Read more'),
                    target: get(popupContent.value.button, '0.target', ''),
                }
            }
        }

        return content
    })

    const mode = computed(() => {
        switch (data.value?.colorPalette) {
            case 'color-subtle':
                return 'brand'
            case 'color-invert':
                return 'brand-invert'
            default:
                return data.value?.colorPalette
        }
    })

    const markAsRead = () => {
        popupRead.value = true
    }

    const dismissPopup = () => {
        markAsRead()
        scrollLocked.value = false
        active.value = false
    }

    onMounted(() => {
        nextTick(() => {
            // if there is no popup data, return
            if (!popupData.value) {
                return
            }

            cmsQuery({
                uri: `entry-data/ds_PopupsEntries/${popupData.value?.canonicalUid}`,
                key: `popup-${popupData.value?.canonicalUid}`,
                server: false,
            })
                .then((response) => {
                    data.value = response.data

                    if (popup.value && !popupRead.value) {
                        active.value = true
                        scrollLocked.value = true
                    }
                })
                .catch((error) => {
                    console.error('Error fetching popup data:', error)
                })
        })
    })
</script>
